import { LogoEBrand, LogoWordmarkBrand } from '@eventbrite/legacy-eds.iconography';
import { defineMessage } from 'react-intl';
import React, { createContext, useContext, type PropsWithChildren } from 'react';
import { GlobalNavContextProps, GlobalNavProps } from '../types';
import { defaultNavigation } from './DefaultNavigation';

export const defaultGlobalNavProps: Omit<GlobalNavProps, 'user' | 'env'> = {
    positionSticky: { mobile: false, desktop: false },
    searchBar: {
        renderSearchBar: true,
        desktopLabel: defineMessage({ id: 'Search events', defaultMessage: 'Search events' }),
        mobileLabel: defineMessage({ id: 'Search', defaultMessage: 'Search' }),
    },
    logo: {
        path: '/',
        ariaLabel: defineMessage({ id: 'Home', defaultMessage: 'Home' }),
        desktopLogo: {
            iconType: <LogoWordmarkBrand />,
            iconWidth: '110px',
            iconHeight: 'auto',
            backgroundColor: 'transparent',
            iconTitle: 'Eventbrite',
            iconColor: 'ui-orange',
        },
        mobileLogo: {
            iconType: <LogoEBrand />,
            iconWidth: '24px',
            iconHeight: '24px',
            backgroundColor: 'transparent',
            iconTitle: 'Eventbrite',
            iconColor: 'ui-orange',
        },
    },
};

// Create a context for GlobalNav
const GlobalNavContext = createContext<GlobalNavContextProps | undefined>(undefined);

export const GlobalNavProvider: React.FC<PropsWithChildren<GlobalNavContextProps>> = ({
    children,
    ...props
}) => {
    const mergedProps: GlobalNavProps = {
        ...defaultGlobalNavProps,
        user: undefined,
        env: {},
    };

    for (const key in props) {
        if (props[key] !== undefined) {
            mergedProps[key] = props[key];
        }
    }

    const localeInfo = mergedProps.env?.localeInfo;
    const helpCenterLanguage = localeInfo?.help_center_language ?? 'en-us';
    const tld = localeInfo?.tld ?? '.com';

    const showIntentToggle = props?.intentToggle?.show || false;
    mergedProps['navigation'] =
        props?.navigation || defaultNavigation({ helpCenterLanguage, tld, showIntentToggle });
    mergedProps['intentToggle'] = props?.intentToggle;

    return <GlobalNavContext.Provider value={mergedProps}>{children}</GlobalNavContext.Provider>;
};

export const useGlobalNavContext = () => {
    const globalNavContext = useContext(GlobalNavContext);
    if (!globalNavContext) {
        throw new Error('useGlobalNavContext must be used within a GlobalNavProvider');
    }
    return globalNavContext;
};
